import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Alert, Button, CircularProgress } from "@mui/material";
import { usePantallaTurnos, useTurnosSocket, useVozTurnos } from "../hooks";
import { hayAlertaPreparacion, textoAlertaPedido, textoLlamada } from "../helpers/turnos";
import { SeccionTurnos, TurnoActualCard } from "../components";
import AuthService from "../../../services/auth.service";
import Roles from "../../../config/roles";

const sxPagina = { width: "100%", minHeight: "100vh", backgroundColor: "#f8f9fa", p: { xs: 2, md: 4 } };
const sxTitulo = { fontWeight: 700, color: "#212529", textAlign: "center", mb: 4, fontSize: "clamp(2rem, 5vw, 4rem)" };
const sxSubtitulo = { fontWeight: 700, color: "#212529", textAlign: "center", mb: 2, fontSize: "clamp(1.5rem, 3vw, 2.5rem)" };
const sxCentrado = { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 };
const sxSpinner = { color: "#cc071e" };
const sxTextoCarga = { color: "#6c757d" };
const sxAviso = { maxWidth: 600, mx: "auto", mt: 4, px: 2 };
const sxSecciones = { display: "flex", flexDirection: "column", gap: 5 };

/**
 * Pantalla pública de turnos para la TV: turno actual, últimos llamados y en
 * espera, actualizada por socket y con voz al llamar o al llegar un pedido sin preparar.
 * DECISION: no sigue el patrón de página de listado (sin Card de cabecera, DataGrid,
 * filtros ni vista móvil) porque es solo lectura a pantalla completa para TV. Por la
 * misma razón, el spinner de carga inicial ocupa toda la pantalla: no hay cabecera ni
 * filtros que preservar. Las recargas por socket no muestran spinner.
 */
const PantallaTurnos = () => {
    const idCola = new URLSearchParams(useLocation().search).get("id");
    // DECISION: el display de llamadas (cuenta pantalla_llamadas) está a la vista del
    // público: muestra los turnos pero no los pedidos asociados (estado, preparador,
    // ubicación) ni locuta el aviso de pedido sin preparar. ADMIN y la pantalla del almacén
    // (pantalla_almacen) sí los ven y oyen el aviso al entrar el turno
    const [mostrarDetalles] = useState(
        () => !AuthService.getCurrentUser()?.roles?.includes(Roles.PANTALLA_LLAMADAS.role)
    );
    const { cola, actual, llamados, esperando, loading, error, recargar } = usePantallaTurnos(idCola);
    const { anunciar } = useVozTurnos();

    // La recarga nunca espera a la voz y un fallo de voz no la impide (useVozTurnos)
    const handleLlamado = useCallback((turno) => {
        anunciar(textoLlamada(turno));
        recargar();
    }, [anunciar, recargar]);

    const handleEsperando = useCallback((turno) => {
        if (!Array.isArray(turno?.detalles)) return;
        recargar();
        if (mostrarDetalles && hayAlertaPreparacion(turno.detalles)) anunciar(textoAlertaPedido(turno.numero));
    }, [anunciar, recargar, mostrarDetalles]);

    const handleCambio = useCallback(() => {
        recargar();
    }, [recargar]);

    useTurnosSocket({
        idCola,
        onLlamado: handleLlamado,
        onEsperando: handleEsperando,
        onCambio: handleCambio,
        onConectado: handleCambio,
    });

    if (!idCola) {
        return (
            <Box sx={sxAviso}>
                <Alert severity="warning">Falta la cola: abre esta pantalla desde la lista de colas</Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={sxCentrado}>
                <CircularProgress size={64} sx={sxSpinner} />
                <Typography sx={sxTextoCarga}>Cargando turnos...</Typography>
            </Box>
        );
    }

    if (error || !cola) {
        return (
            <Box sx={sxAviso}>
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={recargar}>Reintentar</Button>}>
                    {error || "No se pudo cargar la cola"}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={sxPagina}>
            <Typography variant="h2" component="h1" sx={sxTitulo}>{cola.nombre}</Typography>
            <Box sx={sxSecciones}>
                <Box component="section">
                    <Typography variant="h4" component="h2" sx={sxSubtitulo}>Turno actual</Typography>
                    <TurnoActualCard turno={actual} mostrarDetalles={mostrarDetalles} />
                </Box>
                <SeccionTurnos titulo="Últimos llamados" turnos={llamados} mostrarMostrador mostrarDetalles={mostrarDetalles} vacio="Sin llamados recientes" />
                <SeccionTurnos titulo="En espera" turnos={esperando} mostrarDetalles={mostrarDetalles} vacio="Nadie en espera" />
            </Box>
        </Box>
    );
};

export default PantallaTurnos;
